import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, Upload, CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export const AfricaRegionPage = () => {
  const countries = [
    { name: 'Kenya', slug: 'kenya' },
    { name: 'Nigeria', slug: 'nigeria-pharma-nutra-registration' },
    { name: 'Tanzania', slug: 'tanzania' },
    { name: 'Uganda', slug: 'uganda-pharma-nutra-registration' },
    { name: 'Benin', slug: 'benin-pharma-nutra-registration' },
    { name: 'Senegal', slug: 'senegal-pharma-nutra-registration' },
    { name: 'Togo', slug: 'togo-pharma-nutra-registration' },
    { name: 'Mozambique', slug: 'mozambique-pharma-nutra-registration' },
    { name: 'Ghana', slug: 'ghana-pharma-nutra-registration' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Africa Pharma Registration | CTD Dossier Kenya Tanzania Uganda Regulatory Affairs</title>
        <meta
          name="description"
          content="Expert CTD/ACTD dossier preparation for pharmaceutical and nutraceutical registration across East, West & Central Africa. Kenya PPB, Tanzania TMDA, Uganda NDA compliance."
        />
        <meta name="keywords" content="africa pharma registration, CTD dossier africa, kenya pharma registration, tanzania dossier, uganda regulatory affairs, africa nutra registration, PPB Kenya, TMDA Tanzania, regulatory compliance africa, pharmaceutical exports africa" />
        
        <meta property="og:title" content="Africa Pharma Registration | CTD Dossier Services Kenya Tanzania Uganda" />
        <meta property="og:description" content="Specialist regulatory affairs partner for pharma and nutra registration across Africa with CTD/ACTD dossiers." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6" style={{ backgroundColor: '#708080', color: 'white' }}>
            Africa Region
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            Regulatory Affairs in Africa
          </h1>
          <p className="text-2xl mb-4" style={{ color: '#505060' }}>
            Your specialist partner for CTD/ACTD dossiers, supplement registration and multi-country regulatory expansion across East, West & Central Africa.
          </p>
          <p className="text-xl mb-12" style={{ color: '#606070' }}>
            We help manufacturers launch pharma and nutraceutical products across Africa with regulator-aligned dossiers, artwork, translations and clear compliance strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('countries').scrollIntoView({ behavior: 'smooth' })} style={{ backgroundColor: '#708080', color: 'white' }} className="text-lg px-8 py-6">
              Explore African Countries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" style={{ borderColor: '#708080', color: '#708080' }}>
                Talk to Our Regulatory Expert
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Africa Matters */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why Africa Matters
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            Africa is one of the fastest-growing pharma & nutra markets globally, driven by:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Rapid population growth',
              'Increasing healthcare access',
              'High burden of NCDs (diabetes, hypertension, cardiac issues)',
              'Large import dependence (80–90% in many markets)',
              'Modernizing regulatory systems (PPB, TMDA, NDA, MCAZ, NMRA)'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: '#F0E0D0' }}>
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#708080' }} />
                <span className="text-lg" style={{ color: '#303040' }}>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xl mt-8 text-center font-medium" style={{ color: '#708080' }}>
            For exporters, Africa offers scale with long-term demand and clear registration pathways.
          </p>
        </div>
      </section>

      {/* Regulatory Landscape */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Regulatory Landscape
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Dossier Format</h3>
              <p style={{ color: '#505060' }}>CTD / ACTD dossier format (Kenya, Uganda, Tanzania). Strong emphasis on GMP, stability, labeling, CoPP/CPP.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Local Requirements</h3>
              <p style={{ color: '#505060' }}>Certain markets require local agent appointment and physical submission kits.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Multi-language</h3>
              <p style={{ color: '#505060' }}>Multi-language artwork (English, French, Portuguese in some regions).</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Harmonization</h3>
              <p style={{ color: '#505060' }}>We stay aligned with EAC (East African Community), ZAZIBONA and emerging digital submission programs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Covered */}
      <section id="countries" className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Countries Covered
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map((country) => (
              <Link
                key={country.slug}
                to={`/country/${country.slug}`}
                className="group block p-6 bg-white rounded-2xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                style={{ borderLeft: '4px solid #708080' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold" style={{ color: '#303040' }}>{country.name}</span>
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" style={{ color: '#708080' }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            What TheApostrophe Does in Africa
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'CTD/ACTD dossier creation',
              'Country-by-country GAP analysis',
              'Label & artwork compliance (English/French)',
              'Translation support (French, Arabic, Portuguese)',
              'Importer/Local partner coordination',
              'Handling deficiencies & regulatory queries',
              'Renewal & lifecycle management'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#708080' }} />
                <span className="text-lg" style={{ color: '#505060' }}>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Products */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Ideal Product Categories
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Pharma</h3>
              <ul className="space-y-2">
                {['Antihypertensives', 'Antidiabetics', 'Antibiotics', 'PPIs / GI', 'Vitamins & hematinics', 'Pain & inflammation'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#708080' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#708080' }}>Nutra</h3>
              <ul className="space-y-2">
                {['Metabolic health', 'Immunity & liver support', 'Women\'s health', 'Prenatal products', 'Multivitamins'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#708080' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#303040' }}>
              Africa Registration FAQs
            </h2>
            <p className="text-lg" style={{ color: '#708080' }}>
              Common questions about pharmaceutical registration in African markets
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  What is the typical registration timeline for Kenya, Tanzania, and Uganda?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Kenya (PPB):</strong> 6-9 months with complete CTD dossier. <strong>Tanzania (TMDA):</strong> 12-18 months due to rigorous review. <strong>Uganda (NDA):</strong> 9-12 months. Timelines depend heavily on dossier completeness and importer coordination.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Do I need French translations for West African countries?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  Yes. Countries like <strong>Benin, Senegal, and Togo</strong> require French translations for labels and package inserts. We provide regulatory-grade French translations with proper pharmaceutical terminology verification.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Is the East African Community (EAC) a single registration pathway?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  Not yet. While the <strong>EAC aims for harmonization</strong>, Kenya, Tanzania, and Uganda still require separate registrations. However, dossier sharing and reliance pathways are emerging. We prepare country-specific CTD/ACTD dossiers aligned with each authority's requirements.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #708080 0%, #809090 100%)' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to expand across Africa?
          </h2>
          <p className="text-2xl mb-8 text-white/90">
            Launch faster with CTD-ready dossiers and a region-wide strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" className="text-lg px-8 py-6 bg-white hover:bg-white/90" style={{ color: '#708080' }}>
                Start Africa Expansion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                <Upload className="mr-2 h-5 w-5" />
                Upload Product List
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
